import React, { useState, useEffect } from 'react';
import { TrendingUp, Info, Loader2 } from 'lucide-react';
import Drawer from '@/components/ui/Drawer';
import Card from '@/components/ui/Card';
import {
  DiscoveredHashtag,
  AttributionTagStatus,
  PromoteHashtagRequest,
} from '@/types/attributionTag';
import { useAttributionTagStore } from '@/stores/attributionTagStore';

interface PromoteHashtagDrawerProps {
  isOpen: boolean;
  hashtag: DiscoveredHashtag | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  description: string;
  status: AttributionTagStatus;
}

const PromoteHashtagDrawer: React.FC<PromoteHashtagDrawerProps> = ({
  isOpen,
  hashtag,
  onClose,
  onSuccess,
}) => {
  const { promoteHashtag, isSaving } = useAttributionTagStore();

  const [formData, setFormData] = useState<FormData>({
    description: '',
    status: 'Active',
  });

  // Reset form when drawer opens or hashtag changes
  useEffect(() => {
    if (isOpen && hashtag) {
      setFormData({
        description: '',
        status: 'Active',
      });
    }
  }, [isOpen, hashtag]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hashtag) return;

    const promoteData: PromoteHashtagRequest = {
      hashtag_text: hashtag.hashtag_text,
      description: formData.description.trim() || undefined,
      status: formData.status,
      account_ids: [], // No longer linking accounts to tags
    };

    const result = await promoteHashtag(promoteData);

    if (result) {
      onSuccess?.();
      onClose();
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!hashtag) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Promote to Attribution Tag"
      description={`Promote ${hashtag.hashtag_text.startsWith('@') ? hashtag.hashtag_text : `#${hashtag.hashtag_text}`} for commercial tracking`}
      icon={<TrendingUp className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
          {/* Hashtag Text (readonly) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {hashtag.hashtag_text.startsWith('@') ? 'Mention' : 'Hashtag'}
            </label>
            <div className="w-full px-2 py-1.5 bg-gray-100 border border-gray-300 rounded-lg text-xs text-gray-700">
              {hashtag.hashtag_text}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              This will be the tag name for the attribution tag
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the purpose of this attribution tag"
              rows={3}
              disabled={isSaving}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Initial Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              disabled={isSaving}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Historical Posts Confirmation */}
          <Card padding="sm" className="bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-blue-900">Historical posts will be tracked</p>
                <p className="text-xs text-blue-700 mt-1">
                  When you promote this {hashtag.hashtag_text.startsWith('@') ? 'mention' : 'hashtag'}, all posts from your customer accounts
                  that use {hashtag.hashtag_text.startsWith('@') ? hashtag.hashtag_text : `#${hashtag.hashtag_text}`} will be tracked by the new Attribution Tag.
                  This includes {hashtag.post_count.toLocaleString()} discovered post{hashtag.post_count !== 1 ? 's' : ''}.
                </p>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Promoting...
                </>
              ) : (
                'Promote to Tag'
              )}
            </button>
          </div>
        </form>
    </Drawer>
  );
};

export default PromoteHashtagDrawer;
